"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileCheck2, Scale, FileSpreadsheet, ShieldCheck, Briefcase, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const procurementServices = [{
  id: "serv-1",
  title: "Adquisiciones y arrendamientos gubernamentales",
  description: "Asesoría en procedimientos de compras públicas, arrendamientos y contratación de servicios para dependencias y proveedores.",
  icon: Briefcase
}, {
  id: "serv-2",
  title: "Preparación y revisión técnica y económica",
  description: "Auditoría exhaustiva de propuestas, solvencia documental, anexos técnicos y propuestas económicas antes de la entrega.",
  icon: FileSpreadsheet
}, {
  id: "serv-3",
  title: "Licitaciones públicas nacionales e internacionales",
  description: "Acompañamiento integral desde la publicación de bases y junta de aclaraciones hasta el acto de fallo y firma de contrato.",
  icon: Scale
}, {
  id: "serv-4",
  title: "Invitaciones restringidas y adjudicaciones",
  description: "Estructuración de expedientes y dictámenes para procedimientos por excepción conforme a la legislación aplicable.",
  icon: FileCheck2
}, {
  id: "serv-5",
  title: "Cumplimiento normativo y Ley de Adquisiciones",
  description: "Verificación de apego estricto a la Ley de Adquisiciones, Arrendamientos y Servicios del Sector Público y normativas locales.",
  icon: ShieldCheck
}];
const keyStandards = ["Auditoría previa de documentación técnica y contable", "Acompañamiento presencial y virtual en juntas de aclaraciones", "Mitigación de riesgos de descalificación o inconformidades", "Alineación con la Ley de Adquisiciones y normatividad vigente"];
export default function Procurement() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        duration: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="servicios-en-licitaciones-y-contratación-pública" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        {/* Header Block */}
        <div className="max-w-3xl mb-14 lg:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-ring/40 bg-accent/30 text-foreground text-xs font-semibold tracking-widest uppercase mb-4">
            <span>Área de Contratación Pública</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight uppercase font-serif text-foreground leading-tight">
            Licitaciones
          </h2>

          <div className="w-12 h-[2px] bg-ring mt-4 mb-5" />

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans">
            Acompañamiento técnico, contable y jurídico integral en compras gubernamentales, 
            diseñado para asegurar el cumplimiento legal y la solvencia de cada propuesta.
          </p>
        </div>

        {/* Main Grid: 2 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column: Visual Asset & Institutional Credibility */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="relative rounded-sm overflow-hidden border border-border bg-muted shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/executive_reviewing_procurement_binders.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/executive_reviewing_procurement_binders.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/executive_reviewing_procurement_binders.png-wvc-sizes" alt="Especialista revisando expedientes y carpetas técnicas de licitación" loading="lazy" decoding="async" className="w-full h-[380px] sm:h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent pointer-events-none" />
              
              <div className="absolute bottom-0 inset-x-0 p-6 text-primary-foreground">
                <span className="text-xs uppercase tracking-wider font-semibold text-accent-foreground bg-accent/90 px-2 py-0.5 rounded-sm inline-block mb-2">
                  Rigor documental
                </span>
                <p className="text-sm sm:text-base font-medium leading-snug">
                  Revisión minuciosa de carpetas técnicas, económicas y legales antes de su radicación institucional.
                </p>
              </div>
            </div>

            {/* Credibility Summary Card */}
            <Card className="rounded-sm border-border bg-card shadow-xs">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold uppercase tracking-wide font-serif text-foreground">
                  Garantía de cumplimiento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                {keyStandards.map((item, idx) => <div key={idx} data-index={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="size-4 text-ring mt-0.5 shrink-0" />
                    <span className="text-sm text-foreground/80 leading-snug font-sans">
                      {item}
                    </span>
                  </div>)}
              </CardContent>
            </Card>

            {/* Direct Consultation Link Card */}
            <div className="p-6 rounded-sm bg-secondary text-secondary-foreground border border-ring/30 shadow-xs">
              <h3 className="text-base font-semibold uppercase tracking-wide font-serif mb-2">
                ¿Requiere asesoría para un concurso activo?
              </h3>
              <p className="text-xs text-secondary-foreground/80 mb-4 leading-relaxed font-sans">
                Nuestros especialistas senior evalúan las bases del procedimiento y diseñan una ruta crítica de preparación.
              </p>
              <Button asChild variant="outline" className="w-full justify-between bg-transparent border-ring text-secondary-foreground hover:bg-ring/15">
                <Link to="/ContactPage">
                  <span>Hablar con un especialista</span>
                  <ArrowRight className="size-4 text-ring" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Detailed Procurement Services */}
          <div className="lg:col-span-7">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.15
          }} className="flex flex-col gap-4">
              {procurementServices.map((service, index) => {
              const IconComponent = service.icon;
              return <motion.div key={service.id} variants={itemVariants} data-index={index}>
                    <Card className="rounded-sm border-border bg-card hover:border-ring/60 transition-[border-color,box-shadow] duration-300 shadow-2xs">
                      <CardContent className="p-6 flex flex-col sm:flex-row gap-5 items-start">
                        <div className="size-11 rounded-sm border border-ring/40 bg-accent/40 flex items-center justify-center shrink-0">
                          <IconComponent className="size-5 text-foreground" />
                        </div>
                        <div className="space-y-1.5 flex-1">
                          <h3 className="text-base sm:text-lg font-semibold uppercase tracking-wide font-serif text-foreground">
                            {service.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                            {service.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </motion.div>

            {/* Bottom Action Bar */}
            <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground font-sans text-center sm:text-left">
                Cumplimiento normativo estricto · Estrategia · Resultados
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground border border-ring hover:bg-ring hover:text-primary transition-colors">
                  <Link to="/ContactPage">
                    Solicitar asesoría
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}